import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { Link, Link as RouterLink, useNavigate } from 'react-router-dom';
import Facts from '../../components/Facts';
import StatusChip from '../../components/StatusChip';
import { useCompactLayout } from '../../hooks/useCompactLayout';
import { useRunsQuery } from '../../services/routing/queries';
import type { RunSummary } from '../../types/routing';
import { formatDuration, plannedDay } from '../../utils/format';

const STATUSES = ['queued', 'running', 'succeeded', 'failed', 'canceled'];

const EMPTY = 'No runs yet — start one with “New run”.';

// A rerun says which run it re-plans, so a pair can be spotted in the listing without opening
// either.
const triggerLabel = (run: RunSummary) => `${run.trigger}${run.rerun_of ? ` of #${run.rerun_of}` : ''}`;

/** One run per card, for a screen too narrow to read one run per row. Each card is a link. */
const RunCards = ({ runs }: { runs: RunSummary[] }) => (
  <Stack spacing={1}>
    {runs.length === 0 && (
      <Paper sx={{ p: 2 }}>
        <Typography color="text.secondary">{EMPTY}</Typography>
      </Paper>
    )}
    {runs.map(run => (
      <Paper
        key={run.run_id}
        component={RouterLink}
        to={`/runs/${run.run_id}`}
        aria-label={`open run ${run.run_id}`}
        sx={{ p: 1.5, display: 'block', color: 'inherit', textDecoration: 'none' }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle1" sx={{ fontWeight: 650 }}>
            #{run.run_id}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
            {triggerLabel(run)}
          </Typography>
          <StatusChip status={run.status} />
        </Stack>
        <Facts
          items={[
            ['tenant', run.tenant ?? 'default'],
            // Blank rather than assumed: a run from before the marker was recorded did not say
            // which world it planned, and "api" is a guess.
            ['source', run.source ?? ''],
            ['day (NY)', plannedDay(run.from_datetime)],
            ['engine', run.engine ?? ''],
            ['routes', `${run.progress.routes_done}/${run.progress.routes_total}`],
            ['rides', run.progress.rides_assigned],
            ['started (UTC)', run.started_at ?? ''],
            ['duration', formatDuration(run.started_at, run.finished_at)]
          ]}
        />
      </Paper>
    ))}
  </Stack>
);

export const RunsPage = () => {
  const [status, setStatus] = useState('');
  const [limit, setLimit] = useState(50);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const navigate = useNavigate();
  const compact = useCompactLayout();

  const { data: runs, isLoading, error } = useRunsQuery(
    { status: status || undefined, limit },
    autoRefresh
  );

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField
          select
          label="Status"
          size="small"
          value={status}
          onChange={event => setStatus(event.target.value)}
          sx={{ minWidth: 140, flexGrow: { xs: 1, sm: 0 } }}
        >
          <MenuItem value="">any</MenuItem>
          {STATUSES.map(value => (
            <MenuItem key={value} value={value}>
              {value}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Limit"
          size="small"
          type="number"
          value={limit}
          onChange={event => setLimit(Math.min(200, Math.max(1, Number(event.target.value) || 1)))}
          inputProps={{ min: 1, max: 200 }}
          sx={{ width: 100, flexGrow: { xs: 1, sm: 0 } }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={autoRefresh}
              onChange={event => setAutoRefresh(event.target.checked)}
            />
          }
          label="Auto-refresh"
        />
        <Button variant="contained" component={Link} to="/runs/new" sx={{ ml: 'auto' }}>
          New run
        </Button>
      </Stack>

      {error && <Alert severity="error">{(error as Error).message}</Alert>}
      {isLoading && <CircularProgress aria-label="loading runs" />}

      {runs && compact && <RunCards runs={runs} />}

      {runs && !compact && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Run</TableCell>
                <TableCell>Trigger</TableCell>
                <TableCell>Tenant</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Planned day (NY)</TableCell>
                <TableCell>Engine</TableCell>
                <TableCell align="right">Routes</TableCell>
                <TableCell align="right">Rides</TableCell>
                <TableCell>Started (UTC)</TableCell>
                <TableCell>Duration</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {runs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11}>
                    <Typography color="text.secondary">{EMPTY}</Typography>
                  </TableCell>
                </TableRow>
              )}
              {runs.map(run => (
                <TableRow
                  key={run.run_id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/runs/${run.run_id}`)}
                >
                  <TableCell>#{run.run_id}</TableCell>
                  <TableCell>{triggerLabel(run)}</TableCell>
                  <TableCell>{run.tenant ?? 'default'}</TableCell>
                  {/* Blank rather than assumed: a run from before the marker was recorded did
                      not say which world it planned, and "api" is a guess. */}
                  <TableCell>{run.source ?? ''}</TableCell>
                  <TableCell>
                    <StatusChip status={run.status} />
                  </TableCell>
                  <TableCell>{plannedDay(run.from_datetime)}</TableCell>
                  <TableCell>{run.engine ?? ''}</TableCell>
                  <TableCell align="right">
                    {run.progress.routes_done}/{run.progress.routes_total}
                  </TableCell>
                  <TableCell align="right">{run.progress.rides_assigned}</TableCell>
                  <TableCell>{run.started_at ?? ''}</TableCell>
                  <TableCell>{formatDuration(run.started_at, run.finished_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
};

export default RunsPage;
